import type { PropsWithChildren } from 'react'
import { useEffect, useRef } from 'react'
import { useMatch, useParams } from 'react-router-dom'
import type { Id as ToastId } from 'react-toastify'
import { toast } from 'react-toastify'
import { useAuth } from '@/auth'
import { useTrackMutation } from '@mote/api'
import {
  TRACK_EVENT_NAMES,
  WEB_STORAGE_PREFIX_KEYS
} from '@mote/helpers/constants'

export function ReferralUsageTracker(props: PropsWithChildren) {
  const params = useParams()
  const referrerCode = params.referrerCode
  const referralErrorToastId = useRef<ToastId>('')
  const match = useMatch('/r/:referralCode')
  const auth = useAuth()
  const track = useTrackMutation()

  function showReferralError() {
    if (!toast.isActive(referralErrorToastId.current)) {
      let unsubscribe: (() => void) | undefined
      unsubscribe = toast.onChange(async payload => {
        switch (payload.status) {
          case 'removed':
            unsubscribe?.()
            break
          case 'added':
            if (payload.type === toast.TYPE.ERROR) {
              await track.mutate!({
                variables: {
                  event: TRACK_EVENT_NAMES.auth.referralCodeUsed,
                  isNewUser: false,
                  isLoggedIn: Boolean(auth.user)
                }
              })
            }
            break
          default:
        }
      })
      referralErrorToastId.current = toast.error(
        'Whoops - The referral link you clicked can only be used when signing up for a new account',
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored'
        }
      )
    }
  }

  useEffect(() => {
    if (!match) {
      return
    }

    if (!auth.user) {
      const lastUserId = window.localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}lastUserId`
      )
      const deviceId = window.localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}anonymousUserId`
      )

      if (!lastUserId && !deviceId) {
        track.mutate!({
          variables: {
            event: TRACK_EVENT_NAMES.auth.referralCodeUsed,
            isNewUser: true,
            isLoggedIn: false
          }
        })
        return
      }
    }

    showReferralError()
  }, [auth.user, match, referrerCode])

  return props.children
}
